/** MOBILAREA AUTOMATĂ A UNEI CAMERE — reguli de așezare, nu un catalog de poze.
 *
 *  CE FACE. Pentru o cameră (inelul ei, ușile și ferestrele ei) întoarce
 *  piesele pe care le-ar pune un arhitect într-o primă schiță: patul cu
 *  noptierele și dulapul în dormitor, canapeaua cu televizorul în living,
 *  frontul de corpuri (cu mașinile de spălat) în bucătărie, cada sau cabina
 *  de duș, vasul și lavoarul în baie. Fiecare
 *  piesă e scrisă în document (`PiesaDoc`), deci după aia se mută, se rotește
 *  și se șterge ca orice altceva — generatorul e un PUNCT DE PORNIRE.
 *
 *  DE CE E NOU. Așezarea veche (`plan/arhMobilier3d::mobilierCamerei`) lucra
 *  pe dreptunghiul camerei fără să știe de uși decât ca baleiaj scăzut din
 *  cadru și punea piesele la coordonate fixe: pe M5 a doua noptieră cădea în
 *  coridor, dulapul stătea peste baleiajul ușii, iar bucătăria primea corpuri
 *  peste ușă. «Avem deja ceva implementat dar face greșeli.»
 *
 *  CUM E ROBUSTĂ. Toată socoteala se face în CADRUL camerei — dreptunghiul cel
 *  mai mare înscris în inel (`arhMobilier::cadru`), cu latura lungă pe `u` și
 *  cea scurtă pe `v`. Cele patru laturi ale lui sunt «pereții». Fiecare piesă
 *  se așază CU SPATELE LA UN PERETE și se verifică, ca dreptunghi, împotriva a
 *  trei lucruri: (1) să stea în cadru, (2) să nu atingă baleiajul niciunei uși
 *  și nici fâșia de trecere din fața ei, (3) să nu atingă nicio piesă pusă
 *  deja. Ce nu încape NU se pune și nu se înghesuie: un dormitor de 7 m² fără
 *  dulap e un răspuns adevărat, unul cu dulapul în ușă nu. Pozițiile se caută
 *  din 5 în 5 cm pornind de la locul preferat (centrul peretelui, un colț), deci
 *  o cameră se mobilează în sub o milisecundă și tot modulul în câteva.
 *
 *  ORIENTAREA PIESEI: în bibliotecă fața fiecărei piese e spre `+y` — ușile
 *  dulapului, șezutul canapelei, picioarele patului. «Cu spatele la peretele
 *  `v-`» înseamnă rotire 0 în cadrul camerei; la `v+` 180°, la `u-` −90°, la
 *  `u+` +90°. Rotirea scrisă în document e a cadrului plus a piesei.
 *
 *  Regulile pe feluri stau fiecare în funcția ei, cu motivul lângă ea. Un fel
 *  fără regulă nu primește nimic.
 */
import { cadru } from "../../plan/arhMobilier";
import { felulPiesei } from "../../plan/arhMobilierPlan";
import { cutiaAnsamblului, scauneleMesei, SCAUN_M } from "./masaCuScaune";
import { inInel, type Camera, type Gol, type PiesaDoc, type Pt } from "../doc";

/** Un dreptunghi în cadrul camerei: `u` pe latura lungă, `v` pe cea scurtă. */
type Cutie = { u0: number; v0: number; u1: number; v1: number };
/** Una din cele patru laturi ale cadrului. */
type Latura = "v-" | "v+" | "u-" | "u+";
const LATURI: Latura[] = ["v-", "v+", "u-", "u+"];

/** O piesă așezată, încă în cadrul camerei. */
type Pusa = { kind: string; w: number; h: number; cutie: Cutie; rot: number; z?: number;
              /** Ansamblul din care face parte — v. `PiesaDoc.grup`. */
              grup?: string };

/** Cât loc trebuie lăsat în fața unei uși care se deschide ÎN ALTĂ cameră:
 *  golul trebuie să rămână trecere. Adâncimea unui om care intră. */
const TRECERE = 0.7;
/** Pasul la care se caută un loc. */
const PAS = 0.05;
/** Spațiul dintre două piese vecine pe același perete. */
const JOC = 0.05;

const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** Două cutii se ating, cu un joc `j` între ele. Cu o toleranță de o miime de
 *  milimetru: două corpuri lipite se termină și încep la aceeași cotă, iar
 *  virgula mobilă le face să se «calce» cu 1e-16. Măsurat: al doilea corp din
 *  frontul de bucătărie nu se punea. */
const EPS = 1e-6;
const seAting = (a: Cutie, b: Cutie, j: number) =>
  a.u0 < b.u1 + j - EPS && b.u0 < a.u1 + j - EPS
  && a.v0 < b.v1 + j - EPS && b.v0 < a.v1 + j - EPS;

/** Distanța de la un punct la cel mai apropiat loc de pe contur. */
function distantaLaContur(p: Pt, inel: readonly Pt[]): number {
  let cea = Infinity;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const vx = b[0] - a[0];
    const vy = b[1] - a[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 < 1e-12 ? 0
      : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
    cea = Math.min(cea, Math.hypot(p[0] - a[0] - vx * t, p[1] - a[1] - vy * t));
  }
  return cea;
}

/** CAMERA, în cadrul ei: transformările, ușile și ferestrele ca dreptunghiuri. */
class Cadru {
  readonly w: number;
  readonly h: number;
  private readonly cx: number;
  private readonly cy: number;
  private readonly cos: number;
  private readonly sin: number;
  readonly deg: number;
  /** Ce nu se poate acoperi: baleiajele ușilor și trecerile lor. */
  readonly interzise: Cutie[] = [];
  /** Pe ce latură cade fiecare ușă, și unde pe ea (coordonata de-a lungul). */
  readonly usi: { latura: Latura; la: number; latime: number }[] = [];
  /** Ferestrele, la fel: sub ele nu se pune nimic înalt. */
  readonly ferestre: { latura: Latura; la: number; latime: number }[] = [];
  readonly puse: Pusa[] = [];

  constructor(readonly cam: Camera, goluri: readonly Gol[]) {
    const k = cadru(cam.inel as number[][]);
    if (!k) throw new Error("camera n-are cadru");
    this.w = k.w; this.h = k.h; this.cx = k.cx; this.cy = k.cy; this.deg = k.deg;
    const rad = (k.deg * Math.PI) / 180;
    this.cos = Math.cos(rad); this.sin = Math.sin(rad);
    for (const g of goluri) {
      // Al camerei? Mijlocul golului la mai puțin de 35 cm de CONTURUL EI —
      // nu de cadru: într-o cameră în L, ușa poate sta pe bucata de contur
      // care nu intră în dreptunghiul înscris, și tot ea hotărăște unde nu se
      // pune dulapul. Măsurat pe M1, dormitorul: ușa e la 0,57 m de latura
      // cadrului și a fost ignorată — dulapul a ieșit chiar în baleiajul ei.
      const mij: Pt = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2];
      if (distantaLaContur(mij, cam.inel) > 0.35) continue;
      const a = this.local(g.a);
      const b = this.local(g.b);
      const m: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      // Latura cadrului de care ține golul — cea mai apropiată. Contează
      // pentru preferințe (un perete «cu ușă» nu primește tăblia patului), nu
      // pentru geometrie.
      const dist: [Latura, number][] = [
        ["v-", Math.abs(m[1] + this.h / 2)], ["v+", Math.abs(m[1] - this.h / 2)],
        ["u-", Math.abs(m[0] + this.w / 2)], ["u+", Math.abs(m[0] - this.w / 2)],
      ];
      dist.sort((p, q) => p[1] - q[1]);
      const latura = dist[0][0];
      const peV = latura === "v-" || latura === "v+";
      const la = peV ? m[0] : m[1];
      if (g.fel === "fereastra") {
        this.ferestre.push({ latura, la, latime: g.latime });
        continue;
      }
      this.usi.push({ latura, la, latime: g.latime });
      // INTERZIS: pătratul baleiajului, DIN GEOMETRIA UȘII — tocurile ei și
      // normala spre cameră —, adus în cadru ca dreptunghi axial. Dacă foaia
      // se deschide în altă cameră, rămâne fâșia de trecere din fața golului.
      const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
      const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
      let n: Pt = [-uy, ux];
      if (!inInel([mij[0] + n[0] * 0.3, mij[1] + n[1] * 0.3], cam.inel)) n = [uy, -ux];
      const seDeschideAici = g.spre ? inInel(g.spre, cam.inel) : true;
      const adanc = (seDeschideAici ? g.latime : TRECERE) + 0.05;
      const colturi = [g.a, g.b, [g.b[0] + n[0] * adanc, g.b[1] + n[1] * adanc] as Pt,
                       [g.a[0] + n[0] * adanc, g.a[1] + n[1] * adanc] as Pt]
        .map((q) => this.local(q));
      this.interzise.push({
        u0: Math.min(...colturi.map((q) => q[0])) - 0.05,
        u1: Math.max(...colturi.map((q) => q[0])) + 0.05,
        v0: Math.min(...colturi.map((q) => q[1])) - 0.05,
        v1: Math.max(...colturi.map((q) => q[1])) + 0.05,
      });
    }
  }

  local(p: Pt): [number, number] {
    const dx = p[0] - this.cx;
    const dy = p[1] - this.cy;
    return [dx * this.cos + dy * this.sin, -dx * this.sin + dy * this.cos];
  }
  lume(u: number, v: number): Pt {
    return [this.cx + u * this.cos - v * this.sin, this.cy + u * this.sin + v * this.cos];
  }

  /** Lungimea unei laturi. */
  lungimea(l: Latura): number {
    return l === "v-" || l === "v+" ? this.w : this.h;
  }
  /** Adâncimea camerei perpendicular pe latură. */
  adancimea(l: Latura): number {
    return l === "v-" || l === "v+" ? this.h : this.w;
  }
  usiPe(l: Latura): number { return this.usi.filter((u) => u.latura === l).length; }
  ferestrePe(l: Latura) { return this.ferestre.filter((f) => f.latura === l); }
  /** Latura din față. */
  opusa(l: Latura): Latura {
    return l === "v-" ? "v+" : l === "v+" ? "v-" : l === "u-" ? "u+" : "u-";
  }
  /** Laturile perpendiculare pe una dată. */
  vecine(l: Latura): Latura[] {
    return l === "v-" || l === "v+" ? ["u-", "u+"] : ["v-", "v+"];
  }

  /** Cutia unei piese lipite de latura `l`, cu centrul la `la` de-a lungul ei,
   *  lată `lat` (de-a lungul) și adâncă `ad` (spre interior), depărtată de
   *  perete cu `ofs`. */
  langaLatura(l: Latura, la: number, lat: number, ad: number, ofs = 0): Cutie {
    switch (l) {
      case "v-": return { u0: la - lat / 2, u1: la + lat / 2, v0: -this.h / 2 + ofs, v1: -this.h / 2 + ofs + ad };
      case "v+": return { u0: la - lat / 2, u1: la + lat / 2, v0: this.h / 2 - ofs - ad, v1: this.h / 2 - ofs };
      case "u-": return { v0: la - lat / 2, v1: la + lat / 2, u0: -this.w / 2 + ofs, u1: -this.w / 2 + ofs + ad };
      default:   return { v0: la - lat / 2, v1: la + lat / 2, u0: this.w / 2 - ofs - ad, u1: this.w / 2 - ofs };
    }
  }
  /** Rotirea (în cadru) a unei piese cu spatele la latura `l`. */
  rotirea(l: Latura): number {
    return l === "v-" ? 0 : l === "v+" ? 180 : l === "u-" ? -90 : 90;
  }

  inCadru(c: Cutie, tol = 1e-6): boolean {
    return c.u0 >= -this.w / 2 - tol && c.u1 <= this.w / 2 + tol
      && c.v0 >= -this.h / 2 - tol && c.v1 <= this.h / 2 + tol;
  }
  /** Cutia e liberă: în cadru, fără uși și fără piese. `joc` e distanța
   *  minimă față de piesele deja puse. */
  libera(c: Cutie, joc = JOC): boolean {
    if (!this.inCadru(c)) return false;
    if (this.interzise.some((z) => seAting(c, z, 0))) return false;
    return !this.puse.some((p) => seAting(c, p.cutie, joc));
  }

  /** PUNE o piesă cu spatele la latura `l`, cât mai aproape de `la` (coordonata
   *  de-a lungul laturii), căutând din 5 în 5 cm în amândouă părțile. `ofs`
   *  depărtează piesa de perete (o masă în mijloc). Întoarce piesa sau `null`
   *  dacă n-are loc nicăieri pe latură. */
  pune(kind: string, l: Latura, la: number, opt: {
    w?: number; h?: number; ofs?: number; joc?: number; z?: number;
    /** Cât din latură se poate cerceta în jurul lui `la`; implicit toată. */
    raza?: number;
    /** Un loc în plus de ținut liber în FAȚA piesei (spațiu de folosire). */
    fata?: number;
    /** Spațiul din față POATE cădea în baleiajul unei uși. Și baleiajul, și
     *  spațiul de folosire sunt podea liberă — se cer goale de PIESE, nu unul
     *  de altul. E regula obiectelor sanitare: în orice baie de apartament
     *  ușa se deschide chiar în fața lavoarului. Măsurat pe M1 (baie de
     *  2 × 2 cu două uși): cabina încape, dar cei 60 cm din fața ei ating
     *  baleiajul cu 8 cm — fără regula asta baia rămâne fără apă. */
    fataPesteUsa?: boolean;
  } = {}): Pusa | null {
    const fel = felulPiesei(kind);
    const w = opt.w ?? fel?.w ?? 1;
    const h = opt.h ?? fel?.h ?? 1;
    const lung = this.lungimea(l);
    const raza = opt.raza ?? lung;
    for (let d = 0; d <= raza + 1e-9; d += PAS) {
      for (const s of d === 0 ? [0] : [-1, 1]) {
        const p = la + s * d;
        if (Math.abs(p) + w / 2 > lung / 2 + 1e-6) continue;
        const cutie = this.langaLatura(l, p, w, h, opt.ofs ?? 0);
        if (!this.libera(cutie, opt.joc)) continue;
        if (opt.fata) {
          const fata = this.langaLatura(l, p, w, opt.fata, (opt.ofs ?? 0) + h);
          if (!this.inCadru(fata)) continue;
          if (opt.fataPesteUsa ? this.puse.some((q) => seAting(fata, q.cutie, 0))
                               : !this.libera(fata, 0)) continue;
        }
        const pusa: Pusa = { kind, w, h, cutie, rot: this.rotirea(l), z: opt.z ?? fel?.z };
        this.puse.push(pusa);
        return pusa;
      }
    }
    return null;
  }

  /** CEL MAI LUNG INTERVAL LIBER de pe latura `l`, pentru piese adânci `ad`
   *  care mai cer `fata` în față: unde nu cade nicio ușă și nicio piesă. Se
   *  cercetează din 5 în 5 cm cu o cutie îngustă. Întors ca `[de, la]` pe
   *  coordonata de-a lungul laturii, sau `null` dacă nu e nimic liber. */
  intervalLiber(l: Latura, ad: number, fata = 0): [number, number] | null {
    const lung = this.lungimea(l);
    let cel: [number, number] | null = null;
    let de: number | null = null;
    const n = Math.round(lung / PAS);
    for (let i = 0; i <= n; i++) {
      const p = -lung / 2 + i * PAS;
      const ok = i < n && this.libera(this.langaLatura(l, p + PAS / 2, PAS, ad), 0)
        && (!fata || this.libera(this.langaLatura(l, p + PAS / 2, PAS, fata, ad), 0));
      if (ok && de == null) de = p;
      if (!ok && de != null) {
        if (!cel || p - de > cel[1] - cel[0]) cel = [de, p];
        de = null;
      }
    }
    return cel;
  }

  /** Cea mai bună latură pentru o piesă lungă: fără uși, destul de lungă, cu
   *  cel mai mult loc liber; la egalitate, cea cerută întâi. */
  lateraPentru(lat: number, ad: number, preferate: Latura[]): Latura | null {
    let cea: Latura | null = null;
    let scor = -Infinity;
    for (const l of preferate) {
      if (this.lungimea(l) < lat || this.adancimea(l) < ad) continue;
      const s = (this.usiPe(l) ? 0 : 10) + this.lungimea(l) * 0.1
        - preferate.indexOf(l) * 0.01;
      if (s > scor) { scor = s; cea = l; }
    }
    return cea;
  }

  /** Piesele, ca rânduri de document. */
  document(): PiesaDoc[] {
    return this.puse.map((p) => {
      const c = this.lume((p.cutie.u0 + p.cutie.u1) / 2, (p.cutie.v0 + p.cutie.v1) / 2);
      return {
        kind: p.kind, x: { a: r3(c[0]) }, y: { a: r3(c[1]) },
        w_m: r3(p.w), h_m: r3(p.h),
        rot_deg: r3((((this.deg + p.rot) % 360) + 360) % 360),
        ...(p.z ? { z_m: r3(p.z) } : {}),
        ...(p.grup ? { grup: p.grup } : {}),
      };
    });
  }
}

// ------------------------------------------------------------------ regulile ---

/** DORMITORUL: pat, noptiere, dulap, comodă cu televizor.
 *
 *  Capul patului stă pe un perete fără ușă, de preferat pe cel de care te
 *  desparte toată lungimea camerei (`v`), ca la picioare să rămână loc de
 *  trecere și de comodă. Patul dublu (1,60) cere 2,60 m perpendicular — 2,00
 *  patul și 0,60 trecerea; sub atât, pat de o persoană. Noptierele stau
 *  lângă cap, pe același perete. Dulapul se pune pe un perete perpendicular pe
 *  pat, ca ușile lui să nu bată în pat; comoda cu televizorul, pe peretele din
 *  fața patului, dacă între ele rămân cel puțin 0,90 m de trecut. */
function dormitor(c: Cadru): void {
  const dublu = c.h >= 2.6 && c.w >= 2.6;
  const pat = dublu ? "pat" : "pat_o_persoana";
  const pw = dublu ? 1.6 : 0.9;
  // Capul: pe `v` dacă adâncimea ajunge, altfel pe `u`. Fără uși dacă se poate.
  const laturi = c.h >= 2.6 ? ["v-", "v+", "u-", "u+"] : ["u-", "u+", "v-", "v+"];
  const cap = c.lateraPentru(pw, 2.6, laturi as Latura[])
    ?? c.lateraPentru(pw, 2.3, laturi as Latura[]);
  if (!cap) return;
  const p = c.pune(pat, cap, 0, { w: pw, h: 2.0, fata: 0.6 });
  if (!p) return;
  const la = cap === "v-" || cap === "v+" ? (p.cutie.u0 + p.cutie.u1) / 2 : (p.cutie.v0 + p.cutie.v1) / 2;
  // Noptierele, lipite de pat, în amândouă părțile.
  for (const s of [-1, 1]) {
    c.pune("noptiera", cap, la + s * (pw / 2 + JOC + 0.45 / 2), { raza: 0.3 });
  }
  // Dulapul: pe un perete perpendicular, cât mai departe de pat.
  for (const l of c.vecine(cap)) {
    for (const w of [1.8, 1.4, 1.0]) {
      const spre = cap === "v-" || cap === "u-" ? 1 : -1;   // spre picioarele patului
      const lung = c.lungimea(l);
      if (c.pune("dulap", l, spre * (lung / 2 - w / 2), { w, fata: 0.6 })) { l && (w); break; }
    }
    if (c.puse.some((x) => x.kind === "dulap")) break;
  }
  // Comoda cu televizorul, în fața patului.
  const fata = c.opusa(cap);
  const comoda = c.pune("comoda_tv", fata, la, { raza: 1.0, fata: 0.9 });
  if (comoda) {
    const m = (cap === "v-" || cap === "v+")
      ? (comoda.cutie.u0 + comoda.cutie.u1) / 2 : (comoda.cutie.v0 + comoda.cutie.v1) / 2;
    // Televizorul, pe perete, deasupra comodei: nu ocupă loc pe jos, deci nu
    // trece prin `libera` — stă pe cota lui.
    c.puse.push({ kind: "tv", w: 1.2, h: 0.2, rot: c.rotirea(fata), z: 1.1,
                  cutie: c.langaLatura(fata, m, 1.2, 0.2, 0.05) });
  }
}

/** LIVINGUL: canapea, televizor, unul sau două fotolii, masă cu patru scaune.
 *
 *  Canapeaua stă cu spatele la peretele lung fără uși; televizorul pe
 *  peretele din fața ei, la cel puțin 2,20 m — sub atât nu se vede și nu se
 *  trece. Măsuța în fața canapelei, fotoliile la capetele ei, întoarse spre
 *  măsuță. Masa CU SCAUNELE EI (v. `masaCuScaune`) numai dacă camera are cel
 *  puțin 16 m² și rămâne o jumătate liberă: ansamblul ocupă 2,14 × 1,44, și
 *  mai cere 0,50 de fiecare parte ca să se poată trage scaunul. */
function living(c: Cadru): void {
  // LĂȚIMEA CANAPELEI SE CERE CATALOGULUI, nu se scrie aici.
  //
  // Era 2,10 scris de trei ori în funcția asta. Când catalogul a trecut la
  // 2,20 (19.10.2026), cele trei cifre au rămas în urmă — iar peretele se
  // verifica pentru o canapea de 2,10 și se punea una de 2,20: pe un living
  // de 15,57 m² din catalog ea nu mai încăpea, `pune` refuza, iar camera
  // rămânea GOALĂ. Un fapt, un loc.
  const latCanapea = felulPiesei("canapea")?.w ?? 2.2;
  // DOUĂ ADÂNCIMI, ca la dormitor (v. `cap`): întâi camera comodă (3,00 m în
  // fața canapelei, cât trebuie ca să încapă și televizorul), apoi una mai
  // strâmtă. Cu o singură încercare, un living de 4,60 × 3,00 cădea pe
  // virgula mobilă a lui «3,00 ≥ 3,0» și rămânea GOL — cu fotoliu, televizor
  // și masă cu tot, deși în el încap toate.
  const spate = c.lateraPentru(latCanapea, 3.0, ["v-", "v+", "u-", "u+"])
    ?? c.lateraPentru(latCanapea, 2.6, ["v-", "v+", "u-", "u+"]);
  if (!spate) return;
  // Canapeaua spre un capăt al peretelui lung, ca cealaltă jumătate să rămână
  // pentru masă; într-o cameră mai mică, pe centru.
  const arie = Math.abs(c.cam.arie ?? c.w * c.h);
  // 5,74 = canapeaua (2,10 pe-atunci) + ANSAMBLUL mesei + 1,50 de margini și
  // trecere. Era 5,20, socotit pe blatul de 1,60; de când scaunele de pe
  // capete ies în lateral, ansamblul cere 0,54 în plus — iar pragul e cel
  // care hotărăște și dacă se împinge canapeaua într-un capăt. Lăsat la 5,20,
  // o cameră de 5,30 ar fi rămas cu canapeaua trasă deoparte ca să facă loc
  // unei mese care pe urmă nu mai încape.
  const cuMasa = arie >= 16 && c.lungimea(spate) >= 5.74;
  const lung = c.lungimea(spate);
  // Pe jumătatea din fața căreia peretele opus N-ARE ușă: acolo stă
  // televizorul, iar o ușă pe peretele televizorului îl trimite pe hol.
  const fataL = c.opusa(spate);
  const usiFata = c.usi.filter((u) => u.latura === fataL).map((u) => u.la);
  const s = usiFata.some((u) => u < 0) && !usiFata.some((u) => u > 0) ? 1
    : usiFata.some((u) => u > 0) && !usiFata.some((u) => u < 0) ? -1 : -1;
  const la = cuMasa ? s * (lung / 2 - latCanapea / 2 - 0.6) : 0;
  const canapea = c.pune("canapea", spate, la, { fata: 0.5 });
  // UN LIVING ÎN CARE NU ÎNCAPE CANAPEAUA NU RĂMÂNE GOL.
  //
  // Se ieșea de-aici: fără loc pentru canapea, camera nu primea NIMIC — nici
  // fotoliu, nici televizor, nici masă. Cât canapeaua avea 2,10 nu s-a văzut;
  // la 2,20 (cerut pe 19.10.2026) un living de 4,60 × 3,00 din catalog a
  // ieșit gol, deși în el încap toate celelalte. Lipsa trebuie să fie a
  // piesei care nu încape, nu a camerei întregi.
  //
  // FOTOLIUL ÎN LOCUL EI, pe același perete: e piesa de șezut care urmează ca
  // mărime, și rămâne o cameră de stat. Dacă nici el nu încape, atunci chiar
  // nu e loc, și camera rămâne goală cu drept.
  if (!canapea) {
    c.pune("fotoliu", spate, 0, { ofs: 0.1, raza: 1.4, joc: 0.1 });
    return;
  }
  const peV = spate === "v-" || spate === "v+";
  const mij = peV ? (canapea.cutie.u0 + canapea.cutie.u1) / 2 : (canapea.cutie.v0 + canapea.cutie.v1) / 2;
  // Măsuța de cafea, la 0,45 în fața canapelei.
  c.pune("masuta_cafea", spate, mij, { ofs: 0.9 + 0.45, raza: 0.2 });
  // Televizorul, în față, cu comoda lui.
  const fata = c.opusa(spate);
  if (c.adancimea(spate) >= 0.9 + 2.2 + 0.42) {
    const comoda = c.pune("comoda_tv", fata, mij, { raza: 1.6 });
    if (comoda) {
      const m = peV ? (comoda.cutie.u0 + comoda.cutie.u1) / 2 : (comoda.cutie.v0 + comoda.cutie.v1) / 2;
      c.puse.push({ kind: "tv", w: 1.2, h: 0.2, rot: c.rotirea(fata), z: 1.1,
                    cutie: c.langaLatura(fata, m, 1.2, 0.2, 0.05) });
    }
  }
  // Masa cu patru scaune, în cealaltă jumătate a camerei — ÎNAINTEA
  // fotoliilor: masa e piesa mare, fotoliile umplu ce rămâne. Se caută loc
  // pentru masă CU SCAUNE — 0,50 pe fiecare latură lungă —, întâi lângă
  // peretele canapelei (lasă liberă trecerea de-a lungul peretelui
  // televizorului, unde e de obicei ușa), apoi centrată pe adâncime.
  if (cuMasa) {
    const latMasa = 1.6;
    // LOCUL CĂUTAT E AL ANSAMBLULUI, nu al blatului: scaunele de pe capete
    // ies acum în lateral (v. `masaCuScaune`), iar căutat pe blat ansamblul
    // ar fi intrat cu ele în canapea. Pe adâncime rămâne regula veche — 0,50
    // de fiecare parte —, fiindcă acolo nu scaunul strâns sub masă cere
    // locul, ci omul care îl trage ca să se așeze; 0,50 e mai mult decât
    // cutia ansamblului (0,27 pe parte), deci el o cuprinde.
    const ansamblu = cutiaAnsamblului(latMasa, 0.9);
    const latAns = ansamblu.w;
    const adMasa = 0.9 + 2 * 0.5;
    const laM = -s * (lung / 2 - latAns / 2 - 0.5);
    const ad = c.adancimea(spate);
    for (const ofs of [0.6, Math.max(0.6, (ad - adMasa) / 2)]) {
      const m = c.pune("masa", spate, laM, { w: latAns, h: adMasa, ofs, raza: 2.5, joc: 0.3 });
      if (!m) continue;
      // Cutia scrisă e a MESEI: locul căutat a fost cu scaunele, dar piesa e
      // blatul, iar scaunele își primesc mai jos cutiile lor. AMÂNDOUĂ
      // măsurile, nu doar adâncimea: de când se caută pe ansamblu, și
      // lățimea cerută (2,14) e mai mare decât blatul, iar lăsată așa masa
      // s-ar fi scris în document cu 54 cm mai lată decât se desenează.
      m.w = latMasa;
      m.h = 0.9;
      const cv = (m.cutie.v0 + m.cutie.v1) / 2;
      const cu = (m.cutie.u0 + m.cutie.u1) / 2;
      m.cutie = peV
        ? { u0: cu - latMasa / 2, u1: cu + latMasa / 2, v0: cv - 0.45, v1: cv + 0.45 }
        : { v0: cv - latMasa / 2, v1: cv + latMasa / 2, u0: cu - 0.45, u1: cu + 0.45 };
      // SCAUNELE SUNT PIESE, nu un desen al mesei.
      //
      // Le desena masa, în afara cutiei ei. Costa două lucruri: pe un document
      // care ARE scaune scrise — cele venite dintr-un import — se vedeau două
      // rânduri de scaune unul peste altul; iar aici, unde cutia mesei se
      // strânge pe blat imediat ce s-a găsit locul, spațiul scaunelor nu mai
      // era al nimănui, deci fotoliul de mai jos putea fi așezat peste ele.
      // Puse ca piese, scaunele se văd o dată și țin locul ocupat.
      //
      // UNDE STAU E REGULA DIN `masaCuScaune`, aceeași pe care o pune paleta
      // și aceeași după care a fost rescris catalogul: trase 18 cm sub blat,
      // pe toate patru laturile, cât încap. Scrisă aici a doua oară, masa
      // pusă de mână și cea pusă de generator ar fi arătat altfel.
      //
      // SE SCRIU ÎNAINTEA MESEI — `c.puse` ține ordinea, iar din ea iese
      // ordinea din document, adică ordinea de desenare: blatul vine peste
      // ele și le acoperă capătul tras sub el. Masa e deja în listă (`pune`
      // a pus-o), deci se MUTĂ la coadă.
      //
      // Regula e scrisă în reperul MESEI — `x` pe lungul blatului — și se
      // întoarce în cel al camerei: când masa stă pe un perete `u`, lungul ei
      // e `v`, iar scaunul privește cu 90° mai încolo.
      const ansambluId = `G${c.puse.length}`;
      m.grup = ansambluId;
      for (const sc of scauneleMesei([cu, cv], latMasa, 0.9)) {
        const [x, y] = sc.c;
        const [su, sv] = peV ? [x, y] : [y, x];
        c.puse.push({
          kind: "scaun", w: SCAUN_M, h: SCAUN_M, grup: ansambluId,
          cutie: { u0: su - SCAUN_M / 2, u1: su + SCAUN_M / 2,
                   v0: sv - SCAUN_M / 2, v1: sv + SCAUN_M / 2 },
          rot: peV ? sc.deg : sc.deg - 90,
        });
      }
      c.puse.splice(c.puse.indexOf(m), 1);
      c.puse.push(m);
      break;
    }
  }
  // Fotoliile: la capetele canapelei, întoarse spre măsuță (cu spatele la
  // același perete, dar rotite cu 90° spre interior). Unul sau două, cât încap.
  let fotolii = 0;
  for (const sf of [-1, 1]) {
    if (fotolii >= 2) break;
    const laF = mij + sf * (latCanapea / 2 + 0.25 + 0.4);
    const f = c.pune("fotoliu", spate, laF, { ofs: 0.9 + 0.1, raza: 0.3, joc: 0.1 });
    if (f) { f.rot = c.rotirea(spate) + (peV ? -sf * 90 : sf * 90); fotolii++; }
  }
}

/** BUCĂTĂRIA: frontul de corpuri pe peretele cel mai lung fără ușă.
 *
 *  Ordinea, de la un capăt: frigider, corp, chiuvetă, mașină de spălat vase,
 *  mașină de spălat rufe, corp, aragaz, corpuri până se termină peretele.
 *  Chiuveta se pune SUB FEREASTRĂ dacă peretele are una — e regula pe care o
 *  știe orice bucătărie — și frigiderul nu stă lângă aragaz. Cele două mașini
 *  stau lipite de chiuvetă fiindcă acolo sunt apa și scurgerea: un singur
 *  traseu de instalații pentru toate trei. Dacă peretele e prea scurt pentru
 *  tot frontul, el continuă în L pe peretele perpendicular, din colț.
 *
 *  MAȘINA DE SPĂLAT RUFE E AICI, NU ÎN BAIE. Așa se proiectează apartamentele
 *  noastre: baia rămâne pentru obiectele sanitare, iar mașina intră în front,
 *  sub blat, ca orice alt corp de 60. */
function bucatarie(c: Cadru): void {
  const rand: { kind: string; w: number }[] = [
    { kind: "frigider", w: 0.7 }, { kind: "corp_bucatarie", w: 0.6 },
    { kind: "chiuveta", w: 0.6 }, { kind: "masina_vase", w: 0.6 },
    { kind: "masina_spalat", w: 0.6 },
    { kind: "corp_sertare", w: 0.6 }, { kind: "aragaz", w: 0.6 },
    { kind: "corp_bucatarie", w: 0.6 },
  ];
  const total = rand.reduce((a, r) => a + r.w, 0);
  // PERETELE FRONTULUI: cel cu cel mai lung interval liber — nu cel mai lung
  // perete. O ușă în colț mușcă un metru din perete; peretele «cel mai lung»
  // putea fi cel în care frontul nu are unde să pornească (M5, bucătăria de
  // 9,4 m²: primul corp cădea în baleiajul ușii și tot frontul sărea pe
  // peretele scurt). Ferestrei i se dă un mic avans: chiuveta sub fereastră.
  let perete: Latura | null = null;
  let interval: [number, number] | null = null;
  let scor = -Infinity;
  for (const l of LATURI) {
    if (c.adancimea(l) < 1.8) continue;
    const iv = c.intervalLiber(l, 0.6, 0.9);
    if (!iv) continue;
    const s = (iv[1] - iv[0]) + (c.ferestrePe(l).length ? 0.3 : 0);
    if (s > scor) { scor = s; perete = l; interval = iv; }
  }
  if (!perete || !interval || interval[1] - interval[0] < 1.9) return;
  const fer = c.ferestrePe(perete).find((f) => f.la > interval![0] && f.la < interval![1]);
  // Frontul pornește din capătul intervalului liber; dacă are fereastră,
  // alunecă astfel încât chiuveta să cadă sub ea — cât încape.
  let start = interval[0];
  if (fer) {
    const pana = rand.slice(0, 2).reduce((a, r) => a + r.w, 0) + 0.6 / 2;
    start = Math.max(interval[0], Math.min(fer.la - pana, interval[1] - total));
  }
  let la = start;
  const ramase: { kind: string; w: number }[] = [];
  for (const r of rand) {
    const p = c.pune(r.kind, perete, la + r.w / 2, { w: r.w, h: 0.6, raza: 0, fata: 0.9, joc: 0 });
    if (p) la += r.w; else ramase.push(r);
  }
  // Ce n-a încăput continuă în L, pe un perete perpendicular, din colțul comun.
  if (ramase.length) {
    for (const l of c.vecine(perete)) {
      let la2 = perete === "v-" || perete === "u-" ? -c.lungimea(l) / 2 + 0.6 : c.lungimea(l) / 2 - 0.6;
      const dir = perete === "v-" || perete === "u-" ? 1 : -1;
      for (const r of [...ramase]) {
        const p = c.pune(r.kind, l, la2 + dir * r.w / 2, { w: r.w, h: 0.6, raza: 0, fata: 0.9, joc: 0 });
        if (p) { la2 += dir * r.w; ramase.splice(ramase.indexOf(r), 1); }
      }
      if (!ramase.length) break;
    }
  }
  // Corpuri suspendate deasupra frontului, pe același perete, cât ține el.
  const jos = c.puse.filter((p) => p.rot === c.rotirea(perete) && p.kind !== "frigider");
  for (const p of jos) {
    const m = perete === "v-" || perete === "v+"
      ? (p.cutie.u0 + p.cutie.u1) / 2 : (p.cutie.v0 + p.cutie.v1) / 2;
    if (p.kind === "aragaz") {
      c.puse.push({ kind: "hota", w: 0.6, h: 0.5, rot: p.rot, z: 1.5,
                    cutie: c.langaLatura(perete, m, 0.6, 0.5) });
    } else if (!(fer && Math.abs(m - fer.la) < fer.latime / 2 + 0.3)) {
      c.puse.push({ kind: "corp_suspendat", w: p.w, h: 0.35, rot: p.rot, z: 1.45,
                    cutie: c.langaLatura(perete, m, p.w, 0.35) });
    }
  }
}

/** BAIA: cadă sau cabină de duș, vas de WC, lavoar — VARIANTA CARE ÎNCAPE.
 *
 *  Cada de 1,70 se pune de-a latul, pe peretele scurt al camerei, dacă ăla are
 *  cel puțin 1,70 — așa închide un capăt și lasă restul liber; altfel pe
 *  peretele lung. DACĂ NU ÎNCAPE, SAU DACĂ LÂNGĂ EA NU MAI ÎNCAP VASUL ȘI
 *  LAVOARUL, BAIA PRIMEȘTE O CABINĂ DE DUȘ de 0,90 în colț: o baie fără loc
 *  de spălat nu e o baie, iar cabina e răspunsul pe care îl dă orice arhitect
 *  când cada nu intră. Vasul de WC are nevoie de 0,60 în față, lavoarul la
 *  fel; stau pe un perete perpendicular pe apă, vasul în colț, lavoarul lângă
 *  el.
 *
 *  CUM SE ALEGE. Nu «prima cadă care încape»: se încearcă TOATE așezările
 *  apei — cada pe fiecare perete, cabina în fiecare colț —, lângă fiecare se
 *  pun vasul și lavoarul, și rămâne varianta care are vas, apoi apă, apoi
 *  lavoar; la egalitate, cea cu cadă, apoi cea încercată mai devreme. Măsurat pe M1
 *  (2,05 × 2,05, două uși): cada intră pe peretele din stânga, dar apoi nu
 *  mai încape lavoarul; cu cabina de 0,80 în colț încap toate trei. Douăzeci
 *  de variante a câteva zeci de căutări fiecare — sub o milisecundă.
 *
 *  Spațiul din fața fiecărui obiect poate cădea în baleiajul ușii
 *  (`fataPesteUsa`): așa e în orice baie de apartament, și fără asta băile
 *  mici cu două uși rămân fără apă.
 *
 *  Mașina de spălat rufe NU se pune în baie — stă în frontul bucătăriei, v.
 *  `bucatarie`. */
function baie(c: Cadru): void {
  const SAN = { fata: 0.6, fataPesteUsa: true };
  // Așezările apei, în ordinea preferinței.
  const variante: (() => Latura | null)[] = [];
  for (const l of ["u-", "u+", "v-", "v+"] as Latura[]) {
    variante.push(() => {
      if (c.lungimea(l) < 1.7 || c.adancimea(l) < 0.75 + 0.6 || c.usiPe(l)) return null;
      return c.pune("cada", l, 0, { w: 1.7, h: 0.75, ...SAN }) ? l : null;
    });
  }
  // Cabina: în colț, pe un perete fără ușă întâi — ușa unei băi mici e de
  // obicei pe peretele scurt, și cabina în celălalt capăt lasă trecerea.
  // Întâi de 0,90; apoi de 0,80, mărimea minimă standard a unei cabine —
  // pe M1 (2,05 × 2,05, două uși) cei 10 cm sunt exact ce lipsea ca vasul și
  // lavoarul să mai încapă lângă ea.
  const faraUsa = [...LATURI].sort((a, b) => c.usiPe(a) - c.usiPe(b));
  for (const lat of [0.9, 0.8]) {
    for (const l of faraUsa) {
      for (const s of [-1, 1]) {
        variante.push(() =>
          c.pune("dus", l, s * (c.lungimea(l) / 2 - lat / 2), { w: lat, h: lat, raza: 0, ...SAN }) ? l : null);
      }
    }
  }
  // Și fără apă deloc — ultima soluție, dacă nu încape nicăieri.
  variante.push(() => null);

  const n = c.puse.length;
  let cea: Pusa[] = [];
  let scor = -1;
  for (const v of variante) {
    c.puse.length = n;
    const apa = v();
    // Vasul și lavoarul, pe un perete perpendicular pe apă, apoi pe cel opus,
    // și la urmă chiar pe al apei: într-o baie de 2 × 2 cu două uși singurul
    // perete întreg e unul, și pe el stau toate.
    const candidati = apa ? [...c.vecine(apa), c.opusa(apa), apa] : LATURI;
    for (const l of candidati) {
      const lung = c.lungimea(l);
      // Vasul în colțul dinspre partea opusă apei, lavoarul lângă el.
      const wc = c.pune("wc", l, lung / 2 - 0.2 - 0.1, { w: 0.4, h: 0.62, raza: lung, ...SAN })
        ?? c.pune("wc", l, -(lung / 2 - 0.3), { w: 0.4, h: 0.62, raza: lung, ...SAN });
      if (!wc) continue;
      const peV = l === "v-" || l === "v+";
      const m = peV ? (wc.cutie.u0 + wc.cutie.u1) / 2 : (wc.cutie.v0 + wc.cutie.v1) / 2;
      const spre = m > 0 ? -1 : 1;
      c.pune("lavoar", l, m + spre * (0.2 + 0.15 + 0.3), { w: 0.6, h: 0.48, raza: lung, ...SAN });
      break;
    }
    if (!c.puse.some((p) => p.kind === "lavoar")) {
      for (const l of candidati) {
        if (c.pune("lavoar", l, 0, { w: 0.6, h: 0.48, ...SAN })) break;
      }
    }
    // CE CONTEAZĂ MAI MULT: vasul (fără el nu e baie), apoi apa (fără ea e
    // doar un WC), apoi lavoarul (mâinile se spală și la duș); la egalitate,
    // cada înaintea cabinei, iar apoi varianta încercată mai devreme. Într-o
    // baie de 3,9 m² în care două uși mușcă jumătate din cameră încap două
    // obiecte, și cele două trebuie să fie vasul și dușul, nu vasul și
    // lavoarul.
    const puse = c.puse.slice(n);
    const are = (k: string) => puse.some((p) => p.kind === k);
    const s = (are("wc") ? 100 : 0) + (are("cada") || are("dus") ? 10 : 0)
      + (are("lavoar") ? 1 : 0) + (are("cada") ? 0.5 : 0);
    if (s > scor) { scor = s; cea = puse; }
    if (scor >= 111.5) break;   // cadă + vas + lavoar: nu e nimic mai bun de căutat
  }
  c.puse.length = n;
  c.puse.push(...cea);
}

/** Ce fel de cameră primește ce regulă. */
const REGULI: Record<string, (c: Cadru) => void> = {
  bedroom: dormitor, dormitor,
  living,
  kitchen: bucatarie,
  bath: baie, bath2: baie, shower: baie,
};

/** Felurile pe care generatorul știe să le mobileze. */
export const MOBILABILE = new Set(Object.keys(REGULI));

/** MOBILAREA UNEI CAMERE: piesele, ca rânduri de document. Goală pentru un
 *  fel fără regulă sau pentru o cameră fără cadru. `goluri` sunt TOATE golurile
 *  modulului — cadrul își alege singur pe ale lui. */
export function mobileazaCamera(cam: Camera, goluri: readonly Gol[]): PiesaDoc[] {
  const regula = REGULI[cam.kind];
  if (!regula) return [];
  let c: Cadru;
  try { c = new Cadru(cam, goluri); } catch { return []; }
  regula(c);
  // Nimic în afara camerei: cadrul e înscris în inel, deci n-ar trebui să iasă
  // nimic, dar o piesă e o promisiune scrisă — se verifică.
  return c.document().filter((p) => inInel([p.x.a, p.y.a], cam.inel));
}
